import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { api } from "../../api";

export type LoginCredentials = {
  username: string;
  password: string;
};

export type AuthStatus = {
  isAuthenticated: boolean;
};

export const AUTH_STATUS_QUERY_KEY = "auth-status";
const UNAUTHENTICATED_STATUS: AuthStatus = { isAuthenticated: false };

const getAuthStatus = async () => {
  try {
    return await api.get<AuthStatus>("/auth/me").then(({ data }) => data);
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 401) {
      return UNAUTHENTICATED_STATUS;
    }

    throw error;
  }
};

const login = async (credentials: LoginCredentials) =>
  api.post<AuthStatus>("/auth/login", credentials).then(({ data }) => data);

const logout = async () =>
  api.post<AuthStatus>("/auth/logout").then(({ data }) => data);

export const useAuthStatus = () =>
  useQuery<AuthStatus, unknown>({
    queryKey: [AUTH_STATUS_QUERY_KEY],
    queryFn: getAuthStatus,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation<AuthStatus, unknown, LoginCredentials>({
    mutationFn: login,
    onSuccess: (data) => {
      queryClient.setQueryData([AUTH_STATUS_QUERY_KEY], data);
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation<AuthStatus>({
    mutationFn: logout,
    onSuccess: (data) => {
      queryClient.setQueryData([AUTH_STATUS_QUERY_KEY], data);
    },
  });
};
